import { Box, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { BusinessUnitDetails, PaymentSolutionNameRequest, getBusinessUnitDetails } from '../../Api/BoardingApi';
import { useLanguage } from '../../LanguageContext';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';
import Loader from '../Loader/Loader';
import PageContent from '../PageContent/PageContent';
import Sidebar from '../Sidebar/Sidebar';

const RegionSelect: any = () => {

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [businessUnitDetails, setBusinessUnitDetails] = useState<BusinessUnitDetails>({
        businessUnitName: "",
        fullsteamEmail: "",
        logoArray: "",
        languageCultures: []
    });
    const [culture, setCulture] = useState<string>();
    const { paymentSolutionName } = useParams<{ paymentSolutionName: string }>();
    const { base64String } = useParams<{ base64String: string }>();
    const navigate = useNavigate();

    //Handle translation
    const { changeLanguage } = useLanguage();
    const { t } = useTranslation();
    useEffect(() => {
        if (culture) {
            changeLanguage(culture);
        }
    }, [culture]);

    const finishLoading = (elapsedMilliseconds: number) => {
        let waitTime = normalizedWaitMilliseconds(elapsedMilliseconds);
        setTimeout(() => { setIsLoading((isLoading: boolean) => !isLoading) }, waitTime);
    }

    const normalizedWaitMilliseconds = (elapsedMilliseconds: number) => {
        if (elapsedMilliseconds < 0)
            return 0;

        let bufferMilliseconds = 300;
        let normalizedWaitMilliseconds = bufferMilliseconds - elapsedMilliseconds;
        return Math.max(normalizedWaitMilliseconds, 0);
    }

    useEffect(() => {
        let isMounted = true;

        const fetchBusinessUnitDetails = async () => {
            if (!paymentSolutionName)
                return;

            let startTime = new Date().getTime();

            const paymentSolutionNameRequest: PaymentSolutionNameRequest = {
                paymentSolutionName: paymentSolutionName
            };

            try {
                const response: AxiosResponse<BusinessUnitDetails> = await getBusinessUnitDetails(paymentSolutionNameRequest);
                if (isMounted) {
                    setBusinessUnitDetails(response.data);
                    if (response.data.languageCultures.length === 1) {
                        if (base64String) {
                            navigate(`/${paymentSolutionName}/${response.data.languageCultures[0].culture}/${base64String}`);
                        }
                        else {
                            navigate(`/${paymentSolutionName}/${response.data.languageCultures[0].culture}`);
                        }
                    }
                    else if (response.data.languageCultures.length > 0) {
                        //Default to first item from response
                        setCulture(response.data.languageCultures[0].culture);
                    }
                }
            } catch (error) {
                console.error('Error fetching business unit details:', error);
                if (isMounted) {
                    navigate('/error');
                }
            } finally {
                if (isMounted) {
                    let elapsedMilliseconds = new Date().getTime() - startTime;
                    finishLoading(elapsedMilliseconds);
                }
            }
        };

        fetchBusinessUnitDetails();

        return () => {
            isMounted = false;
        };
    }, [paymentSolutionName, navigate]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsLoading(true);
        const culture = (event.target as HTMLInputElement).value;

        if (paymentSolutionName && culture) {
            if (base64String) {
                navigate(`/${paymentSolutionName}/${culture}/${base64String}`);
            }
            else {
                navigate(`/${paymentSolutionName}/${culture}`);
            }
        }
        else {
            navigate(`/error/`);
        }
    };

    document.title = t('Document_title').replace('{{businessUnitName}}', businessUnitDetails?.businessUnitName);
    const heading = t('Page_heading').replace('{{businessUnitName}}', businessUnitDetails?.businessUnitName);
    const pageContentHeading: string = t('RegionSelect_pageContentHeading');

    return (

        <> {isLoading ?
            <Loader /> :
            <Box id="regionSelect">
            <Box className="banner" />
            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                <Grid size={{ xs: 4, sm: 8, md: 4 }}>
                    <Sidebar logo={businessUnitDetails?.logoArray} alt={businessUnitDetails?.businessUnitName} showPanel={false} />
                </Grid>
                <Grid size={{ xs: 4, sm: 8, md: 8 }}>
                    <Header><span id="region-select-business-unit-heading-text">{heading}</span></Header>
                    <PageContent title={pageContentHeading}>
                        <FormControl>
                            <RadioGroup onChange={handleChange} id="region-select-language-cultures">

                                {businessUnitDetails.languageCultures.map((lang, index) => (
                                    <FormControlLabel value={lang.culture} id={`region-select-language-culture-${lang.culture}-label`} data-test-id={`region-select-${lang.culture}-label`} key={`region-select-language-culture-${lang.culture}-label`} control={
                                        <Radio id={`region-select-language-culture-${lang.culture}-radio`} data-test-id={`region-select-${lang.culture}-radio`} key={`region-select-language-culture-${lang.culture}-radio`} />
                                    } label={lang.label} />
                                ))}

                            </RadioGroup>
                        </FormControl>
                        </PageContent>
                    <Footer culture={culture} />
                </Grid>
            </Grid>
        </Box>
        }
        </>
    );
}

export default RegionSelect;